// =============================================
// LLM Provider Abstraction — Types
// v3.0: Dual LLM Engine (Claude + OpenAI)
// =============================================

export type LLMProviderType = "claude" | "openai";

export type ContentEngineStage =
  | "sbd_generation"
  | "scn_audit"
  | "entity_extraction_serp"
  | "data_extraction"
  | "kg_assembly"
  | "fan_out"
  | "fan_out_classification"
  | "fan_out_paa_assignment"
  | "cluster_dedup"
  | "area_consolidation"
  | "writing_profile_generation"
  | "outline"
  | "intro_generation"
  | "section_generation"
  | "draft"
  | "enrichment_claims"
  | "enrichment_verify"
  | "optimization"
  | "optimization_intermediate"
  | "humanization"
  | "seo_meta_generation"
  | "content_audit_llm";

export interface LLMOptions {
  /** Model ID (e.g., "claude-sonnet-4-5", "gpt-5.2") */
  model?: string;
  /** Max output tokens */
  maxTokens?: number;
  /** Temperature (0-1) */
  temperature?: number;
  /** System prompt */
  systemPrompt?: string;
  /** JSON schema for structured output */
  jsonSchema?: Record<string, unknown>;
  /** Timeout in milliseconds */
  timeoutMs?: number;
  /** Reasoning effort for gpt-5.x models (replaces temperature) */
  reasoningEffort?: "low" | "medium" | "high";
}

/**
 * Options for an actual LLM call. Forces every call site to pass `step` for
 * forensics tracking via llm_call_events. mapId/nodeId optional (some calls
 * operate on map-level only, e.g. writing_profile_generation).
 *
 * Step names should match thematic_prompts.step DB enum so future
 * `JOIN llm_call_events ON thematic_prompts.step` works.
 */
export interface LLMCallOptions extends LLMOptions {
  /** REQUIRED — logical step name matching thematic_prompts.step enum */
  step: string;
  /** Map context (if call is part of a thematic map pipeline) */
  mapId?: string;
  /** Node context (per-node LLM calls in Content Engine) */
  nodeId?: string;
  /** Optional linker to thematic_generation_runs or content run.
   *  TODO BUG #2.5: propagate from thematic_generation_runs */
  runId?: string;
}

export interface LLMResponse {
  text: string;
  tokensInput: number;
  tokensOutput: number;
  modelUsed: string;
  provider: LLMProviderType;
  costUsd?: number;
  /** Responses API: response ID for chaining (previous_response_id) */
  responseId?: string;
  /** OpenAI: prompt_tokens_details.cached_tokens; Anthropic: cache_read_input_tokens */
  cachedTokens?: number;
  /** OpenAI reasoning models (gpt-5.x/o1/o3): completion_tokens_details.reasoning_tokens */
  reasoningTokens?: number;
  /** Wall-clock duration of the API call in ms */
  durationMs?: number;
}

/** Options specific to Responses API (draft chaining) */
export interface ResponsesAPIOptions extends LLMCallOptions {
  /** System-level instructions (Responses API: "instructions" field) */
  instructions?: string;
  /** Chain with previous response (Responses API: "previous_response_id") */
  previousResponseId?: string;
  /** Tools for Responses API (e.g. web_search_preview) */
  tools?: Array<{ type: string; [key: string]: unknown }>;
}

export interface LLMProvider {
  readonly type: LLMProviderType;
  call(prompt: string, options: LLMCallOptions): Promise<LLMResponse>;
  /** Responses API call with chaining support (OpenAI only) */
  callResponses?(input: string, options: ResponsesAPIOptions): Promise<LLMResponse>;
}

/**
 * Per-stage model configuration.
 * Maps each Content Engine stage to a provider + model combo.
 */
export interface StageModelConfig {
  provider: LLMProviderType;
  model: string;
}

/**
 * Default model assignments per stage (from KONFIGURACJA-PBN.md).
 */
export const DEFAULT_STAGE_MODELS: Record<ContentEngineStage, StageModelConfig> = {
  // SCN Automation — Claude
  sbd_generation: { provider: "claude", model: "claude-sonnet-5" },
  scn_audit: { provider: "claude", model: "claude-haiku-4-5" },
  // Content Engine — OpenAI per PIPELINE-MAPA-PELNA.md
  entity_extraction_serp: { provider: "openai", model: "gpt-4.1" },
  data_extraction: { provider: "openai", model: "gpt-4.1-mini" },
  kg_assembly: { provider: "openai", model: "gpt-4.1" },
  fan_out: { provider: "openai", model: "gpt-4.1-mini" },
  fan_out_classification: { provider: "openai", model: "gpt-4.1-mini" },
  fan_out_paa_assignment: { provider: "openai", model: "gpt-4.1-mini" },
  cluster_dedup: { provider: "openai", model: "gpt-4.1-mini" },
  area_consolidation: { provider: "openai", model: "gpt-4.1-mini" },
  writing_profile_generation: { provider: "openai", model: "gpt-5.4-mini" },
  outline: { provider: "openai", model: "gpt-4.1" },
  draft: { provider: "openai", model: "gpt-5.4" },
  intro_generation: { provider: "openai", model: "gpt-5.4" },
  section_generation: { provider: "openai", model: "gpt-5.4" },
  enrichment_claims: { provider: "openai", model: "gpt-4.1-mini" },
  enrichment_verify: { provider: "openai", model: "gpt-5.4" },
  optimization: { provider: "openai", model: "gpt-4.1" },
  optimization_intermediate: { provider: "openai", model: "gpt-4.1" },
  humanization: { provider: "openai", model: "gpt-5.4" },
  // SEO meta — gpt-4.1 ze Structured Outputs daje strict JSON + stabilny polish copy + tani.
  seo_meta_generation: { provider: "openai", model: "gpt-4.1" },
  // Content audit LLM (Layer 6) — gpt-4.1-mini, klasyfikacja + extraction zadanie.
  content_audit_llm: { provider: "openai", model: "gpt-4.1-mini" },
};
